import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  Grid,
  Tabs,
  Tab,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CloudQueue as CloudIcon,
  Phone as PhoneIcon,
  VpnKey as KeyIcon,
  Link as LinkIcon,
  Send as SendIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  AudioFile as AudioIcon,
  InsertDriveFile as DocumentIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Campaign as CampaignIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  MarkEmailRead,
  MarkEmailUnread,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Speed as SpeedIcon,
  VerifiedUser as VerifiedUserIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl, getWebSocketUrl } from '../config/environment';
import { ConnectWhatsAppEmbeddedButton } from '../components/whatsapp/ConnectWhatsAppEmbeddedButton';
import { io, Socket } from 'socket.io-client';

// Interfaces
interface WhatsAppCloudApiConfig {
  id: string;
  name: string;
  phone_number_id: string;
  access_token: string;
  business_account_id?: string;
  phone_number: string;
  display_phone_number?: string;
  verified_name?: string;
  api_version: string;
  webhook_verify_token?: string;
  webhook_url?: string;
  status: 'active' | 'inactive' | 'error';
  is_primary: boolean;
  priority: number;
  daily_limit: number;
  daily_sent: number;
  last_reset_date: string;
  quality_rating?: string;
  last_activity?: string;
  error_message?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface Campaign {
  id: string;
  name: string;
  description?: string;
  config_id?: string;
  template_name: string;
  template_language_code: string;
  template_components?: any[];
  source_type: 'csv' | 'txt' | 'crm_stage' | 'manual';
  source_data: any;
  delay_between_sends: number;
  batch_size: number;
  status: 'draft' | 'scheduled' | 'running' | 'paused' | 'completed' | 'cancelled' | 'error';
  total_contacts: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  skipped_count: number;
  current_index: number;
  progress_percentage: number;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  paused_at?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
  whatsapp_cloud_api_configs?: { name: string; phone_number: string };
}

interface CRMStage {
  id: string;
  name: string;
  color: string;
  pipeline_id: string;
  leads_count: number;
  crm_pipelines?: { id: string; name: string };
}

interface Template {
  name: string;
  language: string;
  status: string;
  category: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cloud-api-tabpanel-${index}`}
      aria-labelledby={`cloud-api-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const WhatsAppCloudApi: React.FC = () => {
  const { mode } = useCustomTheme();
  const [tabValue, setTabValue] = useState(0);
  const [configs, setConfigs] = useState<WhatsAppCloudApiConfig[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [crmStages, setCrmStages] = useState<CRMStage[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCampaignDialog, setOpenCampaignDialog] = useState(false);
  const [openCampaignTypeDialog, setOpenCampaignTypeDialog] = useState(false);
  const [selectedCampaignType, setSelectedCampaignType] = useState<'template' | '24h_window' | null>(null);
  const [openCampaignDetailsDialog, setOpenCampaignDetailsDialog] = useState(false);
  const [selectedCampaignForDetails, setSelectedCampaignForDetails] = useState<Campaign | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<WhatsAppCloudApiConfig | null>(null);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' | 'info' | 'warning' 
  });
  
  // Estado para envio de mensagem de teste
  const [sendMessageData, setSendMessageData] = useState({
    phone_number: '',
    message: '',
    config_id: '',
    message_type: 'text' as 'text' | 'template' | 'image' | 'video' | 'audio' | 'document',
    template_name: '',
    template_language_code: 'pt_BR',
    media_url: '',
    media_caption: '',
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Estado para nova campanha
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    config_id: '',
    campaign_type: 'template' as 'template' | '24h_window',
    template_name: '',
    template_language_code: 'pt_BR',
    template_type: 'marketing' as 'marketing' | 'utility',
    utility_template_config: {
      variable_count: 1,
      has_video: false,
      has_image: false,
      has_button: false,
      button_sub_type: 'url' as 'url' | 'quick_reply',
      variables: ['', '', '', '', '', '', ''],
      video_url: '',
      image_url: '',
      button_text: '',
    },
    message_text: '', // Para campanhas de 24h_window
    source_type: 'csv' as 'csv' | 'txt' | 'crm_stage' | 'manual' | 'all_contacts' | 'contacts_24h' | 'contacts_36h' | 'contacts_48h',
    stage_id: '',
    delay_between_sends: 5,
    phone_numbers: '',
    has_media_header: false,
    media_type: 'video' as 'video' | 'image' | 'document',
    media_url: '',
    scheduled_at: '',
  });
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [txtFile, setTxtFile] = useState<File | null>(null);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const txtFileInputRef = useRef<HTMLInputElement>(null);

  // Estado para expandir linha de configuração e ver status detalhado
  const [expandedConfigId, setExpandedConfigId] = useState<string | null>(null);
  const [configStatus, setConfigStatus] = useState<Record<string, any>>({});
  const [loadingStatus, setLoadingStatus] = useState<string | null>(null);

  // WebSocket para atualizações em tempo real
  const socketRef = useRef<Socket | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone_number_id: '',
    access_token: '',
    business_account_id: '',
    phone_number: '',
    display_phone_number: '',
    verified_name: '',
    api_version: 'v24.0',
    webhook_verify_token: '',
    webhook_url: '',
    is_primary: false,
    priority: 1,
    daily_limit: 1000,
  });

  const apiUrl = getApiUrl();
  const wsUrl = getWebSocketUrl();

  // Conectar WebSocket
  useEffect(() => {
    socketRef.current = io(wsUrl, {
      transports: ['websocket', 'polling'],
    });

    socketRef.current.on('campaign_status_update', (data: any) => {
      setCampaigns(prev => prev.map(c => 
        c.id === data.campaignId ? { ...c, status: data.status } : c
      ));
    });

    socketRef.current.on('campaign_progress_update', (data: any) => {
      setCampaigns(prev => prev.map(c => 
        c.id === data.campaignId ? { 
          ...c, 
          sent_count: data.sent_count,
          failed_count: data.failed_count,
          progress_percentage: data.progress_percentage,
        } : c
      ));
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, [wsUrl]);

  useEffect(() => {
    fetchConfigs();
  }, []);

  useEffect(() => {
    if (tabValue === 1) {
      fetchCampaigns();
      fetchCRMStages();
    }
  }, [tabValue]);

  // Carregar templates automaticamente quando dialog abrir (se houver configuração principal)
  useEffect(() => {
    if (openCampaignDialog && !campaignForm.config_id) {
      const primaryConfig = configs.find(c => c.is_primary);
      if (primaryConfig) {
        fetchTemplates();
      }
    }
  }, [openCampaignDialog, configs]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/whatsapp-cloud-api/configs`);
      if (response.data.success) {
        setConfigs(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching Cloud API configs:', error);
      showSnackbar('Erro ao carregar configurações', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      setLoadingCampaigns(true);
      const response = await axios.get(`${apiUrl}/cloud-api-campaigns`);
      if (response.data.success) {
        setCampaigns(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching campaigns:', error);
      showSnackbar('Erro ao carregar campanhas', 'error');
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const fetchCRMStages = async () => {
    try {
      const response = await axios.get(`${apiUrl}/cloud-api-campaigns/helpers/crm-stages`);
      if (response.data.success) {
        setCrmStages(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching CRM stages:', error);
    }
  };

  const fetchTemplates = async (configId?: string) => {
    try {
      const params = configId ? { config_id: configId } : {};
      const response = await axios.get(`${apiUrl}/cloud-api-campaigns/helpers/templates`, { params });
      if (response.data.success) {
        setTemplates(response.data.data || []);
      }
    } catch (error: any) {
      setTemplates([]);
      if (error.response?.status !== 404) {
        console.error('Error fetching templates:', error);
      }
    }
  };

  const handleOpenDialog = (config?: WhatsAppCloudApiConfig) => {
    if (config) {
      setSelectedConfig(config);
      setFormData({
        name: config.name,
        phone_number_id: config.phone_number_id,
        access_token: '', // Não mostrar token existente
        business_account_id: config.business_account_id || '',
        phone_number: config.phone_number,
        display_phone_number: config.display_phone_number || '',
        verified_name: config.verified_name || '',
        api_version: config.api_version,
        webhook_verify_token: config.webhook_verify_token || '',
        webhook_url: config.webhook_url || '',
        is_primary: config.is_primary,
        priority: config.priority,
        daily_limit: config.daily_limit,
      });
    } else {
      setSelectedConfig(null);
      setFormData({
        name: '',
        phone_number_id: '',
        access_token: '',
        business_account_id: '',
        phone_number: '',
        display_phone_number: '',
        verified_name: '',
        api_version: 'v24.0',
        webhook_verify_token: '',
        webhook_url: '',
        is_primary: false,
        priority: 1,
        daily_limit: 1000,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedConfig(null);
    setFormData({
      name: '',
      phone_number_id: '',
      access_token: '',
      business_account_id: '',
      phone_number: '',
      display_phone_number: '',
      verified_name: '',
      api_version: 'v24.0',
      webhook_verify_token: '',
      webhook_url: '',
      is_primary: false,
      priority: 1,
      daily_limit: 1000,
    });
  };

  const handleSave = async () => {
    try {
      // Validações
      if (!formData.name || !formData.phone_number_id || !formData.phone_number) {
        showSnackbar('Preencha todos os campos obrigatórios', 'error');
        return;
      }

      // Se for criação, access_token é obrigatório
      if (!selectedConfig && !formData.access_token) {
        showSnackbar('Access Token é obrigatório para criar uma nova configuração', 'error');
        return;
      }

      const payload: any = { ...formData };
      
      // Se for edição e não forneceu novo token, remover do payload
      if (selectedConfig && !formData.access_token) {
        delete payload.access_token;
      }

      if (selectedConfig) {
        // Atualizar
        await axios.put(`${apiUrl}/whatsapp-cloud-api/configs/${selectedConfig.id}`, payload);
        showSnackbar('Configuração atualizada com sucesso', 'success');
      } else {
        // Criar
        await axios.post(`${apiUrl}/whatsapp-cloud-api/configs`, payload);
        showSnackbar('Configuração criada com sucesso', 'success');
      }

      handleCloseDialog();
      fetchConfigs();
    } catch (error: any) {
      console.error('Error saving config:', error);
      showSnackbar(error.response?.data?.error || 'Erro ao salvar configuração', 'error');
    }
  };

  const handleToggleExpand = async (configId: string) => {
    if (expandedConfigId === configId) {
      setExpandedConfigId(null);
      return;
    }
    setExpandedConfigId(configId);
    // Usar cache se já carregado
    if (configStatus[configId]) return;
    try {
      setLoadingStatus(configId);
      const response = await axios.get(`${apiUrl}/whatsapp-cloud-api/configs/${configId}/status`);
      if (response.data.success) {
        setConfigStatus(prev => ({ ...prev, [configId]: response.data.data }));
      }
    } catch (error: any) {
      console.error('Error fetching config status:', error);
      setConfigStatus(prev => ({
        ...prev,
        [configId]: { error: error.response?.data?.error || 'Erro ao buscar status' }
      }));
    } finally {
      setLoadingStatus(null);
    }
  };

  const handleRefreshStatus = async (configId: string) => {
    try {
      setLoadingStatus(configId);
      const response = await axios.get(`${apiUrl}/whatsapp-cloud-api/configs/${configId}/status`);
      if (response.data.success) {
        setConfigStatus(prev => ({ ...prev, [configId]: response.data.data }));
      }
    } catch (error: any) {
      console.error('Error refreshing config status:', error);
      showSnackbar(error.response?.data?.error || 'Erro ao atualizar status', 'error');
    } finally {
      setLoadingStatus(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar esta configuração?')) {
      return;
    }

    try {
      await axios.delete(`${apiUrl}/whatsapp-cloud-api/configs/${id}`);
      showSnackbar('Configuração deletada com sucesso', 'success');
      fetchConfigs();
    } catch (error: any) {
      console.error('Error deleting config:', error);
      showSnackbar('Erro ao deletar configuração', 'error');
    }
  };

  const handleTest = async (id: string) => {
    try {
      setTesting(id);
      const response = await axios.post(`${apiUrl}/whatsapp-cloud-api/configs/${id}/test`);
      if (response.data.success) {
        showSnackbar('Configuração testada com sucesso', 'success');
        fetchConfigs(); // Atualizar dados
      }
    } catch (error: any) {
      console.error('Error testing config:', error);
      showSnackbar(error.response?.data?.error || 'Erro ao testar configuração', 'error');
    } finally {
      setTesting(null);
    }
  };

  const getQualityRatingColor = (rating?: string) => {
    switch (rating) {
      case 'GREEN':
        return 'success';
      case 'YELLOW':
        return 'warning';
      case 'RED':
        return 'error';
      default:
        return 'default';
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('embedded_signup') === '1') {
        setSnackbar({
          open: true,
          message: 'WhatsApp conectado com sucesso via Meta (Embedded Signup).',
          severity: 'success',
        });
        params.delete('embedded_signup');
        const qs = params.toString();
        const path = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, '', path);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Função para lidar com seleção de arquivo
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tamanho (50MB)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      showSnackbar('Arquivo muito grande. Tamanho máximo: 50MB', 'error');
      return;
    }

    // Validar tipo
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/mpeg', 'video/quicktime',
      'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/aac', 'audio/m4a',
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      showSnackbar('Tipo de arquivo não suportado', 'error');
      return;
    }

    setSelectedFile(file);
    
    // Criar preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setMediaPreview(e.target?.result as string);
        setSendMessageData({ ...sendMessageData, message_type: 'image' });
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('video/')) {
      setMediaPreview(URL.createObjectURL(file));
      setSendMessageData({ ...sendMessageData, message_type: 'video' });
    } else if (file.type.startsWith('audio/')) {
      setSendMessageData({ ...sendMessageData, message_type: 'audio' });
    } else {
      setSendMessageData({ ...sendMessageData, message_type: 'document' });
    }
  };

  // Função para upload de mídia
  const handleUploadMedia = async (): Promise<string | null> => {
    if (!selectedFile) return null;

    try {
      setUploadingMedia(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${apiUrl}/files/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.data?.url) {
        return response.data.data.url;
      }
      
      showSnackbar('Erro ao fazer upload da mídia', 'error');
      return null;
    } catch (error: any) {
      console.error('Error uploading media:', error);
      showSnackbar(error.response?.data?.error || 'Erro ao fazer upload da mídia', 'error');
      return null;
    } finally {
      setUploadingMedia(false);
    }
  };

  // Função para remover mídia selecionada
  const handleRemoveMedia = () => {
    setSelectedFile(null);
    setMediaPreview(null);
    setSendMessageData({ ...sendMessageData, message_type: 'text', media_url: '', media_caption: '' });
  };

  const handleSendTestMessage = async () => {
    if (!sendMessageData.phone_number) {
      showSnackbar('Preencha o número de telefone', 'warning');
      return;
    }

    if (sendMessageData.message_type === 'text' && !sendMessageData.message) {
      showSnackbar('Preencha a mensagem', 'warning');
      return;
    }

    if (sendMessageData.message_type === 'template' && !sendMessageData.template_name) {
      showSnackbar('Preencha o nome do template', 'warning');
      return;
    }

    // Validar mídia se necessário
    if (['image', 'video', 'audio', 'document'].includes(sendMessageData.message_type)) {
      if (!selectedFile && !sendMessageData.media_url) {
        showSnackbar('Selecione um arquivo de mídia ou forneça uma URL', 'warning');
        return;
      }
    }

    try {
      setSendingMessage(true);
      
      // Upload de mídia se houver arquivo selecionado
      let mediaUrl: string | null = sendMessageData.media_url || null;
      if (selectedFile && !mediaUrl) {
        mediaUrl = await handleUploadMedia();
        if (!mediaUrl) {
          setSendingMessage(false);
          return;
        }
      }

      const payload: any = {
        phone_number: sendMessageData.phone_number,
        config_id: sendMessageData.config_id || undefined,
      };

      if (sendMessageData.message_type === 'template') {
        payload.template_name = sendMessageData.template_name;
        payload.template_language_code = sendMessageData.template_language_code;
      } else if (['image', 'video', 'audio', 'document'].includes(sendMessageData.message_type)) {
        payload.message_type = sendMessageData.message_type;
        payload.media_url = mediaUrl;
        if (sendMessageData.media_caption) {
          payload.media_caption = sendMessageData.media_caption;
        }
      } else {
        payload.message = sendMessageData.message;
      }

      const response = await axios.post(`${apiUrl}/whatsapp-cloud-api/send-message`, payload);

      if (response.data.success) {
        showSnackbar('Mensagem enviada com sucesso!', 'success');
        setSendMessageData({
          phone_number: '',
          message: '',
          config_id: '',
          message_type: 'text',
          template_name: '',
          template_language_code: 'pt_BR',
          media_url: '',
          media_caption: '',
        });
        setSelectedFile(null);
        setMediaPreview(null);
        fetchConfigs(); // Atualizar contadores
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      showSnackbar(error.response?.data?.error || error.response?.data?.details || 'Erro ao enviar mensagem', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  // =====================================================
  // CAMPANHAS
  // =====================================================

  const handleOpenCampaignDialog = () => {
    // Abrir dialog de seleção de tipo de campanha
    setOpenCampaignTypeDialog(true);
  };

  const handleSelectCampaignType = (type: 'template' | '24h_window') => {
    setSelectedCampaignType(type);
    setOpenCampaignTypeDialog(false);
    setEditingCampaignId(null);
    setCampaignForm({
      name: '',
      description: '',
      config_id: '',
      campaign_type: type,
      template_name: '',
      template_language_code: 'pt_BR',
      template_type: 'marketing',
      utility_template_config: {
        variable_count: 1,
        has_video: false,
        has_image: false,
        has_button: false,
        button_sub_type: 'url',
        variables: ['', '', '', '', '', '', ''],
        video_url: '',
        image_url: '',
        button_text: '',
      },
      message_text: '',
      source_type: 'csv' as 'csv' | 'txt' | 'crm_stage' | 'manual' | 'all_contacts' | 'contacts_24h' | 'contacts_36h' | 'contacts_48h',
      stage_id: '',
      delay_between_sends: 5,
      phone_numbers: '',
      has_media_header: false,
      media_type: 'video',
      media_url: '',
      scheduled_at: '',
    });
    setCsvFile(null);
    setTxtFile(null);
    setTemplates([]);
    setOpenCampaignDialog(true);
  };

  const handleCloseCampaignDialog = () => {
    setOpenCampaignDialog(false);
    setCsvFile(null);
    setTxtFile(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.name.endsWith('.csv')) {
        setCsvFile(file);
      }
    }
  };

  const handleTxtFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTxtFile(file);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const campaignType = campaignForm.campaign_type || 'template';

      if (!campaignForm.name) {
        showSnackbar('Nome da campanha é obrigatório', 'warning');
        return;
      }

      // Validações específicas por tipo de campanha
      if (campaignType === 'template') {
        if (!campaignForm.template_name || campaignForm.template_name.trim() === '') {
          showSnackbar('Nome do template é obrigatório', 'warning');
          return;
        }
      } else if (campaignType === '24h_window') {
        if (!campaignForm.config_id) {
          showSnackbar('Selecione uma configuração Cloud API', 'warning');
          return;
        }
        if (!campaignForm.message_text || !campaignForm.message_text.trim()) {
          showSnackbar('Digite o texto da mensagem', 'warning');
          return;
        }
      }

      if (campaignForm.source_type === 'csv' && !csvFile) {
        showSnackbar('Selecione um arquivo CSV', 'warning');
        return;
      }

      if (campaignForm.source_type === 'txt' && !txtFile) {
        showSnackbar('Selecione um arquivo TXT', 'warning');
        return;
      }

      if (campaignForm.source_type === 'crm_stage' && !campaignForm.stage_id) {
        showSnackbar('Selecione um estágio do CRM', 'warning');
        return;
      }

      if (campaignForm.source_type === 'manual' && !campaignForm.phone_numbers.trim()) {
        showSnackbar('Digite os números de telefone', 'warning');
        return;
      }

      // Validações para novos tipos de fonte (não precisam de validação adicional)
      if (['all_contacts', 'contacts_24h', 'contacts_36h', 'contacts_48h'].includes(campaignForm.source_type)) {
        // Não precisa de validação adicional, apenas buscar contatos
      }

      // Validações específicas para template de utilidade (apenas para campanhas de template)
      if (campaignType === 'template' && campaignForm.template_type === 'utility') {
        // Validar variáveis
        const requiredVariables = campaignForm.utility_template_config.variables.slice(0, campaignForm.utility_template_config.variable_count);
        if (requiredVariables.some(v => !v.trim())) {
          showSnackbar('Preencha todas as variáveis obrigatórias', 'warning');
          return;
        }
        
        // Validar vídeo se marcado
        if (campaignForm.utility_template_config.has_video && !campaignForm.utility_template_config.video_url.trim()) {
          showSnackbar('Insira a URL do vídeo', 'warning');
          return;
        }
        
        // Validar imagem se marcada
        if (campaignForm.utility_template_config.has_image && !campaignForm.utility_template_config.image_url.trim()) {
          showSnackbar('Insira a URL da imagem', 'warning');
          return;
        }
        
        // Validar botão se marcado
        if (campaignForm.utility_template_config.has_button && !campaignForm.utility_template_config.button_text.trim()) {
          showSnackbar('Insira o texto do botão', 'warning');
          return;
        }
      }

      // Validação para template de marketing com mídia (apenas para campanhas de template)
      if (campaignType === 'template' && campaignForm.template_type === 'marketing' && campaignForm.has_media_header && !campaignForm.media_url.trim()) {
        showSnackbar('Insira a URL da mídia do header', 'warning');
        return;
      }

      setCreatingCampaign(true);

      // Montar componentes do template apenas para templates de marketing com mídia
      let templateComponents: any[] = [];
      if (campaignForm.template_type === 'marketing' && campaignForm.has_media_header && campaignForm.media_url) {
        templateComponents = [
          {
            type: 'header',
            parameters: [
              {
                type: campaignForm.media_type,
                [campaignForm.media_type]: {
                  link: campaignForm.media_url
                }
              }
            ]
          }
        ];
      }

      // Preparar payload base
      const basePayload: any = {
        name: campaignForm.name,
        description: campaignForm.description,
        config_id: campaignForm.config_id || undefined,
        campaign_type: campaignType,
        delay_between_sends: campaignForm.delay_between_sends,
        scheduled_at: campaignForm.scheduled_at ? new Date(campaignForm.scheduled_at).toISOString() : undefined,
      };

      // Adicionar configurações específicas por tipo de campanha
      if (campaignType === 'template') {
        basePayload.template_name = campaignForm.template_name;
        basePayload.template_language_code = campaignForm.template_language_code;
        basePayload.template_type = campaignForm.template_type;
        
        if (campaignForm.template_type === 'marketing') {
          basePayload.template_components = templateComponents.length > 0 ? templateComponents : undefined;
        } else if (campaignForm.template_type === 'utility') {
          basePayload.utility_template_config = {
            variable_count: campaignForm.utility_template_config.variable_count,
            has_video: campaignForm.utility_template_config.has_video,
            has_image: campaignForm.utility_template_config.has_image,
            has_button: campaignForm.utility_template_config.has_button,
            button_sub_type: campaignForm.utility_template_config.button_sub_type,
            variables: campaignForm.utility_template_config.variables.slice(0, campaignForm.utility_template_config.variable_count),
            video_url: campaignForm.utility_template_config.video_url || undefined,
            image_url: campaignForm.utility_template_config.image_url || undefined,
            button_text: campaignForm.utility_template_config.button_text || undefined,
          };
        }
      } else if (campaignType === '24h_window') {
        basePayload.message_text = campaignForm.message_text;
      }

      let campaignId: string;

      if (editingCampaignId) {
        const updateResponse = await axios.put(`${apiUrl}/cloud-api-campaigns/${editingCampaignId}`, basePayload);
        campaignId = updateResponse.data.data.id;
        showSnackbar('Campanha atualizada com sucesso!', 'success');
      } else {
        basePayload.source_type = campaignForm.source_type;
        basePayload.source_data = campaignForm.source_type === 'crm_stage' 
          ? { stage_id: campaignForm.stage_id }
          : {};
        
        const createResponse = await axios.post(`${apiUrl}/cloud-api-campaigns`, basePayload);
        campaignId = createResponse.data.data.id;
      }

      if (!editingCampaignId) {
        if (campaignForm.source_type === 'csv' && csvFile) {
          const formData = new FormData();
          formData.append('file', csvFile);
          await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/import-csv`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else if (campaignForm.source_type === 'txt' && txtFile) {
          const formData = new FormData();
          formData.append('file', txtFile);
          await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/import-txt`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else if (campaignForm.source_type === 'crm_stage') {
          await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/import-crm`, {
            stage_id: campaignForm.stage_id,
          });
        } else if (campaignForm.source_type === 'manual') {
          const numbers = campaignForm.phone_numbers
            .split(/[\n,;]/)
            .map(n => n.trim())
            .filter(n => n);
          await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/import-manual`, {
            phone_numbers: numbers,
          });
        } else if (campaignForm.source_type === 'all_contacts') {
          await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/import-by-hours`, {
            hours: null,
          });
        } else if (campaignForm.source_type === 'contacts_24h') {
          await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/import-by-hours`, {
            hours: 24,
          });
        } else if (campaignForm.source_type === 'contacts_36h') {
          await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/import-by-hours`, {
            hours: 36,
          });
        } else if (campaignForm.source_type === 'contacts_48h') {
          await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/import-by-hours`, {
            hours: 48,
          });
        }
      }

      if (!editingCampaignId) {
        showSnackbar('Campanha criada com sucesso!', 'success');
      }
      handleCloseCampaignDialog();
      fetchCampaigns();
    } catch (error: any) {
      console.error('Error creating campaign:', error);
      showSnackbar(error.response?.data?.error || 'Erro ao criar campanha', 'error');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleStartCampaign = async (campaignId: string) => {
    try {
      await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/start`);
      showSnackbar('Campanha iniciada!', 'success');
      fetchCampaigns();
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Erro ao iniciar campanha', 'error');
    }
  };

  const handlePauseCampaign = async (campaignId: string) => {
    try {
      await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/pause`);
      showSnackbar('Campanha pausada', 'info');
      fetchCampaigns();
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Erro ao pausar campanha', 'error');
    }
  };

  const handleResumeCampaign = async (campaignId: string) => {
    try {
      await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/resume`);
      showSnackbar('Campanha retomada!', 'success');
      fetchCampaigns();
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Erro ao retomar campanha', 'error');
    }
  };

  const handleCancelCampaign = async (campaignId: string) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta campanha?')) {
      return;
    }
    try {
      await axios.post(`${apiUrl}/cloud-api-campaigns/${campaignId}/cancel`);
      showSnackbar('Campanha cancelada', 'info');
      fetchCampaigns();
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Erro ao cancelar campanha', 'error');
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta campanha?')) {
      return;
    }
    try {
      await axios.delete(`${apiUrl}/cloud-api-campaigns/${campaignId}`);
      showSnackbar('Campanha excluída', 'success');
      fetchCampaigns();
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Erro ao excluir campanha', 'error');
    }
  };

  const handleViewCampaignDetails = async (campaignId: string) => {
    try {
      const response = await axios.get(`${apiUrl}/cloud-api-campaigns/${campaignId}`);
      setSelectedCampaignForDetails(response.data.data);
      setOpenCampaignDetailsDialog(true);
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Erro ao carregar detalhes da campanha', 'error');
    }
  };

  const handleEditCampaign = async (campaign: Campaign) => {
    try {
      // Carregar dados completos da campanha
      const response = await axios.get(`${apiUrl}/cloud-api-campaigns/${campaign.id}`);
      const fullCampaign = response.data.data;
      
      // Extrair dados do template de utilidade se existir
      const utilityConfig = fullCampaign.utility_template_config || {
        variable_count: 1,
        has_video: false,
        has_image: false,
        has_button: false,
        button_sub_type: 'url',
        variables: ['', '', '', '', '', '', ''],
        video_url: '',
        image_url: '',
        button_text: '',
      };

      // Se for template de utilidade, extrair variáveis dos components (até 7 variáveis)
      if (fullCampaign.template_type === 'utility' && fullCampaign.template_components) {
        const bodyComponent = fullCampaign.template_components.find((c: any) => c.type === 'body');
        if (bodyComponent && bodyComponent.parameters) {
          const fromApi = bodyComponent.parameters.map((p: any) => p.text || '');
          utilityConfig.variables = [...fromApi, ...Array(7).fill('')].slice(0, 7);
          utilityConfig.variable_count = Math.min(bodyComponent.parameters.length, 7);
        }

        const headerComponent = fullCampaign.template_components.find((c: any) => c.type === 'header');
        if (headerComponent && headerComponent.parameters) {
          const param = headerComponent.parameters[0];
          if (param.video) {
            utilityConfig.has_video = true;
            utilityConfig.video_url = param.video.link || '';
          } else if (param.image) {
            utilityConfig.has_image = true;
            utilityConfig.image_url = param.image.link || '';
          }
        }

        const buttonComponent = fullCampaign.template_components.find((c: any) => c.type === 'button');
        if (buttonComponent) {
          utilityConfig.has_button = true;
          utilityConfig.button_sub_type = buttonComponent.sub_type || 'url';
          utilityConfig.button_text = buttonComponent.parameters?.[0]?.text || '';
        }
      }
      
      const campaignType = fullCampaign.campaign_type || 'template';
      
      // Preencher formulário com dados da campanha
      setCampaignForm({
        name: fullCampaign.name || campaign.name,
        description: fullCampaign.description || '',
        config_id: fullCampaign.config_id || '',
        campaign_type: campaignType,
        template_name: fullCampaign.template_name || '',
        template_language_code: fullCampaign.template_language_code || 'pt_BR',
        template_type: fullCampaign.template_type || 'marketing',
        utility_template_config: utilityConfig,
        message_text: fullCampaign.message_text || '',
        source_type: fullCampaign.source_type || 'csv',
        stage_id: fullCampaign.source_data?.stage_id || '',
        delay_between_sends: fullCampaign.delay_between_sends || 5,
        phone_numbers: '',
        has_media_header: campaignType === 'template' && fullCampaign.template_type === 'marketing' && 
                         (fullCampaign.template_components?.some((c: any) => c.type === 'header') || false),
        media_type: fullCampaign.template_components?.[0]?.parameters?.[0]?.type === 'video' ? 'video' : 
                   fullCampaign.template_components?.[0]?.parameters?.[0]?.type === 'image' ? 'image' : 'document',
        media_url: fullCampaign.template_components?.[0]?.parameters?.[0]?.video?.link || 
                   fullCampaign.template_components?.[0]?.parameters?.[0]?.image?.link || 
                   fullCampaign.template_components?.[0]?.parameters?.[0]?.document?.link || '',
        scheduled_at: fullCampaign.scheduled_at ? new Date(fullCampaign.scheduled_at).toISOString().slice(0, 16) : '',
      });
      
      if (fullCampaign.config_id) {
        await fetchTemplates(fullCampaign.config_id);
      } else {
        await fetchTemplates();
      }
      
      setEditingCampaignId(campaign.id);
      setOpenCampaignDialog(true);
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Erro ao carregar campanha para edição', 'error');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'completed':
        return 'success';
      case 'inactive':
      case 'draft':
        return 'default';
      case 'error':
      case 'cancelled':
        return 'error';
      case 'running':
        return 'primary';
      case 'paused':
      case 'scheduled':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      backgroundColor: 'transparent', 
      minHeight: '100vh',
      color: 'text.primary',
      position: 'relative',
      zIndex: 1,
    }}>
      {/* Header com título */}
      <Box sx={{ 
        mb: { xs: 2, md: 3 }, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 0 },
        padding: { xs: '16px', md: '20px 24px' },
        position: 'relative',
        zIndex: 1,
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            color: '#000000',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            mb: 0.5,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <CloudIcon sx={{ fontSize: { xs: 24, md: 32 }, color: '#8b5cf6' }} />
            WhatsApp Cloud API
          </Typography>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Gerencie configurações e campanhas da API Oficial do WhatsApp
          </Typography>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<CloudIcon />} label="Configurações" iconPosition="start" />
          <Tab icon={<CampaignIcon />} label="Campanhas" iconPosition="start" />
        </Tabs>
      </Box>

      {/* TAB 0: CONFIGURAÇÕES */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1, flexWrap: 'wrap' }}>
          <ConnectWhatsAppEmbeddedButton
            variant="outlined"
            returnPath="/whatsapp-cloud-api"
            openInNewTab
            onPrepareError={(err) => showSnackbar(err.message, 'error')}
            onFinishError={(err) => showSnackbar(err.message, 'error')}
            onFinishSuccess={({ displayPhoneNumber }) => {
              showSnackbar(
                displayPhoneNumber
                  ? `WhatsApp conectado: ${displayPhoneNumber}`
                  : 'WhatsApp conectado com sucesso.',
                'success'
              );
              void fetchConfigs();
            }}
            sx={{
              borderColor: 'rgba(37, 211, 102, 0.5)',
              color: '#128C7E',
              '&:hover': { borderColor: '#25D366', backgroundColor: 'rgba(37, 211, 102, 0.08)' },
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchConfigs}
            disabled={loading}
            sx={{
              borderColor: 'rgba(139, 92, 246, 0.3)',
              color: '#000000',
              '&:hover': {
                borderColor: 'rgba(139, 92, 246, 0.5)',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
              }
            }}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#8b5cf6',
              '&:hover': {
                backgroundColor: '#1da851',
              }
            }}
          >
            Nova Configuração Manual
          </Button>
      </Box>

      {/* Card para envio de mensagem de teste */}
      <Card className="glass-blue-card" sx={{ 
        mb: 3,
      }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SendIcon />
            Enviar Mensagem de Teste
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Envie uma mensagem de texto ou template para testar o envio via Cloud API
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Número de Telefone *"
                value={sendMessageData.phone_number}
                onChange={(e) => setSendMessageData({ ...sendMessageData, phone_number: e.target.value })}
                placeholder="5511999999999"
                helperText="Formato: 55 + DDD + número (sem espaços ou caracteres especiais)"
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(139, 92, 246, 0.08)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    borderRadius: '8px',
                    color: '#000000',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(139, 92, 246, 0.4)',
                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                    },
                    '&.Mui-focused': {
                      borderColor: 'rgba(139, 92, 246, 0.6)',
                      boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: 'rgba(139, 92, 246, 0.8)',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#94a3b8',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Tipo de Mensagem *"
                value={sendMessageData.message_type}
                onChange={(e) => {
                  const newType = e.target.value as 'text' | 'template' | 'image' | 'video' | 'audio' | 'document';
                  setSendMessageData({ ...sendMessageData, message_type: newType });
                  if (!['image', 'video', 'audio', 'document'].includes(newType)) {
                    setSelectedFile(null);
                    setMediaPreview(null);
                  }
                }}
                SelectProps={{
                  native: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(139, 92, 246, 0.08)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    borderRadius: '8px',
                    color: '#000000',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(139, 92, 246, 0.4)',
                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                    },
                    '&.Mui-focused': {
                      borderColor: 'rgba(139, 92, 246, 0.6)',
                      boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& select': {
                      color: '#000000',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: 'rgba(139, 92, 246, 0.8)',
                    },
                  },
                }}
              >
                <option value="text">Mensagem de Texto</option>
                <option value="template">Template</option>
                <option value="image">📷 Imagem</option>
                <option value="video">🎥 Vídeo</option>
                <option value="audio">🎵 Áudio</option>
                <option value="document">📄 Documento</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Configuração (Opcional)"
                value={sendMessageData.config_id}
                onChange={(e) => setSendMessageData({ ...sendMessageData, config_id: e.target.value })}
                SelectProps={{
                  native: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Deixe em branco para usar a configuração principal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(139, 92, 246, 0.08)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    borderRadius: '8px',
                    color: '#000000',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(139, 92, 246, 0.4)',
                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                    },
                    '&.Mui-focused': {
                      borderColor: 'rgba(139, 92, 246, 0.6)',
                      boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& select': {
                      color: '#000000',
                      padding: '16.5px 14px',
                      paddingRight: '32px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: 'rgba(139, 92, 246, 0.8)',
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#94a3b8',
                    marginTop: '4px',
                    marginLeft: 0,
                    marginRight: 0,
                    marginBottom: 0,
                  },
                }}
              >
                <option value="">Usar configuração principal</option>
                {configs
                  .filter((config) => config.status === 'active')
                  .map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.name} ({config.phone_number})
                    </option>
                  ))}
              </TextField>
            </Grid>
            
            {sendMessageData.message_type === 'text' ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mensagem *"
                  value={sendMessageData.message}
                  onChange={(e) => setSendMessageData({ ...sendMessageData, message: e.target.value })}
                  placeholder="Digite sua mensagem aqui..."
                  multiline
                  rows={3}
                  helperText={`${sendMessageData.message.length} caracteres`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(139, 92, 246, 0.08)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid rgba(139, 92, 246, 0.2)',
                      borderRadius: '8px',
                      color: '#000000',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(139, 92, 246, 0.4)',
                        boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                      },
                      '&.Mui-focused': {
                        borderColor: 'rgba(139, 92, 246, 0.6)',
                        boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                      '& textarea': {
                        color: '#000000',
                        '&::placeholder': {
                          color: '#94a3b8',
                          opacity: 1,
                        },
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#64748b',
                      '&.Mui-focused': {
                        color: 'rgba(139, 92, 246, 0.8)',
                      },
                    },
                    '& .MuiFormHelperText-root': {
                      color: '#94a3b8',
                    },
                  }}
                />
              </Grid>
            ) : sendMessageData.message_type === 'template' ? (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nome do Template *"
                    value={sendMessageData.template_name}
                    onChange={(e) => setSendMessageData({ ...sendMessageData, template_name: e.target.value })}
                    placeholder="hello_world"
                    helperText="Nome do template aprovado no WhatsApp"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(139, 92, 246, 0.08)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(139, 92, 246, 0.2)',
                        borderRadius: '8px',
                        color: '#000000',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'rgba(139, 92, 246, 0.4)',
                          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                        },
                        '&.Mui-focused': {
                          borderColor: 'rgba(139, 92, 246, 0.6)',
                          boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
                        },
                        '& fieldset': {
                          border: 'none',
                        },
                        '& input': {
                          color: '#000000',
                          '&::placeholder': {
                            color: '#94a3b8',
                            opacity: 1,
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#64748b',
                        '&.Mui-focused': {
                          color: 'rgba(139, 92, 246, 0.8)',
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#94a3b8',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Código do Idioma"
                    value={sendMessageData.template_language_code}
                    onChange={(e) => setSendMessageData({ ...sendMessageData, template_language_code: e.target.value })}
                    placeholder="pt_BR"
                    helperText="Código do idioma do template (ex: pt_BR, en_US)"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(139, 92, 246, 0.08)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(139, 92, 246, 0.2)',
                        borderRadius: '8px',
                        color: '#000000',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'rgba(139, 92, 246, 0.4)',
                          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                        },
                        '&.Mui-focused': {
                          borderColor: 'rgba(139, 92, 246, 0.6)',
                          boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)',
                        },
                        '& fieldset': {
                          border: 'none',
                        },
                        '& input': {
                          color: '#000000',
                          '&::placeholder': {
                            color: '#94a3b8',
                            opacity: 1,
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#64748b',
                        '&.Mui-focused': {
                          color: 'rgba(139, 92, 246, 0.8)',
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#94a3b8',
                      },
                    }}
                  />
                </Grid>
              </>
            ) : ['image', 'video', 'audio', 'document'].includes(sendMessageData.message_type) ? (
              <>
                <Grid item xs={12}>
                  <Box sx={{ 
                    p: 2, 
                    border: '1px dashed rgba(139, 92, 246, 0.3)', 
                    borderRadius: 2,
                    background: 'rgba(31, 41, 55, 0.2)',
                  }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {sendMessageData.message_type === 'image' && <ImageIcon />}
                      {sendMessageData.message_type === 'video' && <VideoIcon />}
                      {sendMessageData.message_type === 'audio' && <AudioIcon />}
                      {sendMessageData.message_type === 'document' && <DocumentIcon />}
                      Upload de {sendMessageData.message_type === 'image' ? 'Imagem' : sendMessageData.message_type === 'video' ? 'Vídeo' : sendMessageData.message_type === 'audio' ? 'Áudio' : 'Documento'}
                    </Typography>
                    
                    {!selectedFile && !sendMessageData.media_url && (
                      <Box>
                        <input
                          accept={
                            sendMessageData.message_type === 'image' ? 'image/*' :
                            sendMessageData.message_type === 'video' ? 'video/*' :
                            sendMessageData.message_type === 'audio' ? 'audio/*' :
                            'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                          }
                          style={{ display: 'none' }}
                          id="media-upload-button"
                          type="file"
                          onChange={handleFileSelect}
                        />
                        <label htmlFor="media-upload-button">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadIcon />}
                            sx={{
                              borderColor: 'rgba(139, 92, 246, 0.3)',
                              color: '#000000',
                              '&:hover': {
                                borderColor: 'rgba(139, 92, 246, 0.5)',
                                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                              }
                            }}
                          >
                            Selecionar Arquivo
                          </Button>
                        </label>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                          Ou forneça uma URL direta:
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          label="URL da Mídia"
                          value={sendMessageData.media_url}
                          onChange={(e) => setSendMessageData({ ...sendMessageData, media_url: e.target.value })}
                          placeholder="https://..."
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    )}
                    
                    {selectedFile && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                          </Typography>
                          <IconButton size="small" onClick={handleRemoveMedia} sx={{ color: 'error.main' }}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                        {mediaPreview && sendMessageData.message_type === 'image' && (
                          <Box sx={{ mt: 2 }}>
                            <img 
                              src={mediaPreview} 
                              alt="Preview" 
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: 300, 
                                borderRadius: 8,
                                objectFit: 'contain'
                              }} 
                            />
                          </Box>
                        )}
                        {mediaPreview && sendMessageData.message_type === 'video' && (
                          <Box sx={{ mt: 2 }}>
                            <video 
                              src={mediaPreview} 
                              controls 
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: 300, 
                                borderRadius: 8
                              }} 
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                    
                    {sendMessageData.media_url && !selectedFile && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="URL da Mídia"
                          value={sendMessageData.media_url}
                          onChange={(e) => setSendMessageData({ ...sendMessageData, media_url: e.target.value })}
                          placeholder="https://..."
                          InputProps={{
                            endAdornment: (
                              <IconButton size="small" onClick={() => setSendMessageData({ ...sendMessageData, media_url: '' })}>
                                <CloseIcon />
                              </IconButton>
                            )
                          }}
                        />
                      </Box>
                    )}
                    
                    <TextField
                      fullWidth
                      size="small"
                      label="Legenda (Opcional)"
                      value={sendMessageData.media_caption}
                      onChange={(e) => setSendMessageData({ ...sendMessageData, media_caption: e.target.value })}
                      placeholder="Adicione uma legenda para a mídia..."
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  </Box>
                </Grid>
              </>
            ) : null}
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={sendingMessage || uploadingMedia ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleSendTestMessage}
                disabled={
                  sendingMessage || 
                  uploadingMedia ||
                  !sendMessageData.phone_number || 
                  (sendMessageData.message_type === 'text' && !sendMessageData.message) ||
                  (sendMessageData.message_type === 'template' && !sendMessageData.template_name) ||
                  (['image', 'video', 'audio', 'document'].includes(sendMessageData.message_type) && !selectedFile && !sendMessageData.media_url)
                }
                sx={{ minWidth: 150 }}
              >
                {sendingMessage || uploadingMedia ? 'Enviando...' : 'Enviar Mensagem'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : configs.length === 0 ? (
        <Card className="glass-blue-card" sx={{
        }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CloudIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Nenhuma configuração encontrada
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Crie uma nova configuração para começar a usar a WhatsApp Cloud API
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Criar Configuração
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} className="glass-blue-card" sx={{
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2), 0 2px 6px rgba(139, 92, 246, 0.1)',
          '& .MuiTableHead-root': {
            '& .MuiTableCell-root': {
              color: '#1e293b',
              fontWeight: 600,
              borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
              backgroundColor: 'rgba(139, 92, 246, 0.08)',
            }
          },
          '& .MuiTableBody-root': {
            '& .MuiTableCell-root': {
              color: '#0f172a',
              borderBottom: '1px solid rgba(139, 92, 246, 0.1)',
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: 'rgba(139, 92, 246, 0.05)',
            }
          }
        }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40, px: 1 }} />
                <TableCell>Nome</TableCell>
                <TableCell>Número</TableCell>
                <TableCell>Phone Number ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Qualidade</TableCell>
                <TableCell>Principal</TableCell>
                <TableCell>Enviadas Hoje</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <React.Fragment key={config.id}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedConfigId === config.id ? 'none' : undefined } }}
                    onClick={() => handleToggleExpand(config.id)}
                  >
                    <TableCell sx={{ width: 40, px: 1 }}>
                      <IconButton size="small">
                        {expandedConfigId === config.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ color: '#0f172a' }}>
                          {config.name}
                        </Typography>
                        {config.verified_name && (
                          <Typography variant="caption" sx={{ color: '#475569' }}>
                            {config.verified_name}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#0f172a' }}>
                          {config.display_phone_number || config.phone_number}
                        </Typography>
                        {config.display_phone_number && config.display_phone_number !== config.phone_number && (
                          <Typography variant="caption" sx={{ color: '#475569' }}>
                            {config.phone_number}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#0f172a' }}>
                        {config.phone_number_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={config.status}
                        color={getStatusColor(config.status) as any}
                        size="small"
                        icon={config.status === 'active' ? <CheckCircleIcon /> : config.status === 'error' ? <ErrorIcon /> : <WarningIcon />}
                      />
                      {config.error_message && (
                        <Tooltip title={config.error_message}>
                          <ErrorIcon sx={{ ml: 1, fontSize: 16, color: 'error.main' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {config.quality_rating ? (
                        <Chip
                          label={config.quality_rating}
                          color={getQualityRatingColor(config.quality_rating) as any}
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: '#475569' }}>N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {config.is_primary ? (
                        <Chip label="Sim" color="primary" size="small" />
                      ) : (
                        <Typography variant="body2" sx={{ color: '#475569' }}>Não</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#0f172a' }}>
                        {config.daily_sent.toLocaleString()} / {config.daily_limit.toLocaleString()}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((config.daily_sent / config.daily_limit) * 100, 100)}
                        sx={{ mt: 0.5, height: 4, borderRadius: 2, width: 100 }}
                        color={config.daily_sent >= config.daily_limit ? 'error' : 'primary'}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Testar">
                          <IconButton size="small" onClick={() => handleTest(config.id)} disabled={testing === config.id} color="info">
                            {testing === config.id ? <CircularProgress size={20} /> : <CheckCircleIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenDialog(config)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deletar">
                          <IconButton size="small" onClick={() => handleDelete(config.id)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Linha expandida com status detalhado */}
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0, px: 0 }}>
                      <Collapse in={expandedConfigId === config.id} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 3, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                          {loadingStatus === config.id ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Skeleton variant="rectangular" height={30} />
                              <Skeleton variant="rectangular" height={200} />
                            </Box>
                          ) : configStatus[config.id]?.error ? (
                            <Alert severity="error">{configStatus[config.id].error}</Alert>
                          ) : configStatus[config.id] ? (
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PhoneIcon color="primary" />
                                  Status Detalhado — {config.name}
                                </Typography>
                                <Tooltip title="Atualizar status">
                                  <IconButton size="small" onClick={() => handleRefreshStatus(config.id)}>
                                    <RefreshIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>

                              <Grid container spacing={2}>
                                {/* Status do Número */}
                                <Grid item xs={12} md={4}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PhoneIcon fontSize="small" />
                                        Status do Número
                                      </Typography>
                                      <Chip
                                        label={configStatus[config.id].meta?.phone_status?.label || 'N/A'}
                                        color={
                                          configStatus[config.id].meta?.phone_status?.severity === 'success' ? 'success' :
                                          configStatus[config.id].meta?.phone_status?.severity === 'error' ? 'error' :
                                          configStatus[config.id].meta?.phone_status?.severity === 'warning' ? 'warning' :
                                          'default'
                                        }
                                        sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 0.5 }}
                                      />
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                        Raw: {configStatus[config.id].meta?.phone_status?.raw || '-'}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>

                                {/* Qualidade */}
                                <Grid item xs={12} md={4}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <SpeedIcon fontSize="small" />
                                        Qualidade do Número
                                      </Typography>
                                      <Chip
                                        label={configStatus[config.id].meta?.quality_rating?.label || 'N/A'}
                                        color={
                                          configStatus[config.id].meta?.quality_rating?.severity === 'success' ? 'success' :
                                          configStatus[config.id].meta?.quality_rating?.severity === 'error' ? 'error' :
                                          configStatus[config.id].meta?.quality_rating?.severity === 'warning' ? 'warning' :
                                          'default'
                                        }
                                        sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 0.5 }}
                                      />
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                        Rating: {configStatus[config.id].meta?.quality_rating?.raw || '-'}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>

                                {/* Limite de Mensagens */}
                                <Grid item xs={12} md={4}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <SendIcon fontSize="small" />
                                        Limite de Mensagens/Dia (Meta)
                                      </Typography>
                                      <Typography variant="h6" fontWeight="bold" color="primary">
                                        {configStatus[config.id].meta?.messaging_limit?.description || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                        Tier: {configStatus[config.id].meta?.messaging_limit?.tier || '-'}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </Grid>

                                {/* Informações da Meta */}
                                <Grid item xs={12} md={6}>
                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <VerifiedUserIcon fontSize="small" />
                                        Informações da Meta
                                      </Typography>
                                      <List dense disablePadding>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Nome Verificado"
                                            secondary={configStatus[config.id].meta?.verified_name || 'N/A'}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                          />
                                        </ListItem>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Número Exibido"
                                            secondary={configStatus[config.id].meta?.display_phone_number || 'N/A'}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                          />
                                        </ListItem>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Status do Nome"
                                            secondary={configStatus[config.id].meta?.name_status || 'N/A'}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                          />
                                        </ListItem>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Conta Oficial"
                                            secondary={configStatus[config.id].meta?.is_official_business_account ? 'Sim ✓' : 'Não'}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500, color: configStatus[config.id].meta?.is_official_business_account ? 'success.main' : 'text.primary' }}
                                          />
                                        </ListItem>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Modo da Conta"
                                            secondary={configStatus[config.id].meta?.account_mode || 'N/A'}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                          />
                                        </ListItem>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Plataforma"
                                            secondary={configStatus[config.id].meta?.platform_type || 'N/A'}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                          />
                                        </ListItem>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Verificação de Código"
                                            secondary={configStatus[config.id].meta?.code_verification_status || 'N/A'}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                          />
                                        </ListItem>
                                        {configStatus[config.id].meta?.throughput && (
                                          <ListItem disableGutters sx={{ py: 0.25 }}>
                                            <ListItemText
                                              primary="Throughput"
                                              secondary={
                                                typeof configStatus[config.id].meta.throughput === 'object'
                                                  ? `Level: ${configStatus[config.id].meta.throughput.level || 'N/A'}`
                                                  : configStatus[config.id].meta.throughput
                                              }
                                              primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                              secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                            />
                                          </ListItem>
                                        )}
                                      </List>
                                    </CardContent>
                                  </Card>
                                </Grid>

                                {/* WABA + Dados Locais */}
                                <Grid item xs={12} md={6}>
                                  {configStatus[config.id].waba && (
                                    <Card variant="outlined" sx={{ mb: 2 }}>
                                      <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <BusinessIcon fontSize="small" />
                                          Conta Comercial (WABA)
                                        </Typography>
                                        <List dense disablePadding>
                                          <ListItem disableGutters sx={{ py: 0.25 }}>
                                            <ListItemText
                                              primary="Nome da Empresa"
                                              secondary={configStatus[config.id].waba.name || 'N/A'}
                                              primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                              secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                            />
                                          </ListItem>
                                          <ListItem disableGutters sx={{ py: 0.25 }}>
                                            <ListItemText
                                              primary="Status da Conta"
                                              secondary={configStatus[config.id].waba.account_review_status || 'N/A'}
                                              primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                              secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                            />
                                          </ListItem>
                                          <ListItem disableGutters sx={{ py: 0.25 }}>
                                            <ListItemText
                                              primary="Namespace de Templates"
                                              secondary={configStatus[config.id].waba.message_template_namespace || 'N/A'}
                                              primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                              secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                            />
                                          </ListItem>
                                        </List>
                                      </CardContent>
                                    </Card>
                                  )}

                                  <Card variant="outlined">
                                    <CardContent>
                                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CloudIcon fontSize="small" />
                                        Dados Locais
                                      </Typography>
                                      <List dense disablePadding>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Phone Number ID"
                                            secondary={configStatus[config.id].local?.phone_number_id || config.phone_number_id}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                                          />
                                        </ListItem>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Business Account ID"
                                            secondary={configStatus[config.id].local?.business_account_id || config.business_account_id || 'N/A'}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                                          />
                                        </ListItem>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Versão da API"
                                            secondary={configStatus[config.id].local?.api_version || config.api_version}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                          />
                                        </ListItem>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Enviadas Hoje / Limite Local"
                                            secondary={`${config.daily_sent} / ${config.daily_limit}`}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                          />
                                        </ListItem>
                                        <ListItem disableGutters sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primary="Última Atividade"
                                            secondary={config.last_activity ? new Date(config.last_activity).toLocaleString('pt-BR') : 'Nenhuma'}
                                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                          />
                                        </ListItem>
                                        {config.error_message && (
                                          <ListItem disableGutters sx={{ py: 0.25 }}>
                                            <ListItemText
                                              primary="Último Erro"
                                              secondary={config.error_message}
                                              primaryTypographyProps={{ variant: 'caption', color: 'error' }}
                                              secondaryTypographyProps={{ variant: 'body2', color: 'error.main' }}
                                            />
                                          </ListItem>
                                        )}
                                      </List>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              </Grid>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </TabPanel>

      {/* TAB 1: CAMPANHAS */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchCampaigns} disabled={loadingCampaigns}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCampaignDialog}>
            Nova Campanha
          </Button>
        </Box>

        {loadingCampaigns ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : campaigns.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <CampaignIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Nenhuma campanha encontrada
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Crie uma campanha para disparar mensagens em massa via WhatsApp Cloud API
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCampaignDialog}>
                Criar Campanha
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {campaigns.map((campaign) => (
              <Grid item xs={12} md={6} lg={4} key={campaign.id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" noWrap sx={{ maxWidth: 200 }}>
                          {campaign.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Template: {campaign.template_name}
                        </Typography>
                      </Box>
                      <Chip 
                        label={campaign.status} 
                        color={getStatusColor(campaign.status) as any} 
                        size="small" 
                      />
                    </Box>

                    {/* Progresso */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Progresso
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {campaign.progress_percentage.toFixed(1)}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={campaign.progress_percentage} 
                        sx={{ height: 8, borderRadius: 4 }}
                        color={campaign.status === 'error' ? 'error' : 'primary'}
                      />
                    </Box>

                    {/* Estatísticas */}
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PeopleIcon fontSize="small" color="action" />
                          <Typography variant="body2">{campaign.total_contacts} contatos</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircleOutline fontSize="small" color="success" />
                          <Typography variant="body2">{campaign.sent_count} enviadas</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MarkEmailRead fontSize="small" color="info" />
                          <Typography variant="body2">{campaign.delivered_count} entregues</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ErrorOutline fontSize="small" color="error" />
                          <Typography variant="body2">{campaign.failed_count} falhas</Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 1 }} />

                    {/* Ações */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Ver Detalhes">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleViewCampaignDetails(campaign.id)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {campaign.status === 'draft' && (
                          <Tooltip title="Editar">
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleEditCampaign(campaign)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {['draft', 'cancelled', 'completed', 'error'].includes(campaign.status) && (
                          <Tooltip title="Excluir">
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDeleteCampaign(campaign.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {campaign.status === 'draft' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<PlayIcon />}
                            onClick={() => handleStartCampaign(campaign.id)}
                          >
                            Iniciar
                          </Button>
                        )}
                        {campaign.status === 'running' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<PauseIcon />}
                            onClick={() => handlePauseCampaign(campaign.id)}
                          >
                            Pausar
                          </Button>
                        )}
                        {campaign.status === 'paused' && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<PlayIcon />}
                              onClick={() => handleResumeCampaign(campaign.id)}
                            >
                              Retomar
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<StopIcon />}
                              onClick={() => handleCancelCampaign(campaign.id)}
                            >
                              Cancelar
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>

                    {/* Data */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Criada em: {formatDate(campaign.created_at)}
                    </Typography>
                    {campaign.started_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Iniciada: {formatDate(campaign.started_at)}
                      </Typography>
                    )}
                    {campaign.completed_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Concluída: {formatDate(campaign.completed_at)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Dialog de criação/edição */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #8b5cf6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(139, 92, 246, 0.2)',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#8b5cf6',
          color: '#ffffff',
          borderBottom: '2px solid #7c3aed',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}>
          <CloudIcon sx={{ fontSize: 28, color: '#ffffff' }} />
          {selectedConfig ? 'Editar Configuração' : 'Nova Configuração Manual'}
        </DialogTitle>
        <DialogContent sx={{ 
          p: 3,
          backgroundColor: '#ffffff',
        }}>
          {/* Seção: Informações Básicas */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ 
              color: '#8b5cf6', 
              fontWeight: 600, 
              mb: 2,
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Informações Básicas
            </Typography>
            <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number ID *"
                value={formData.phone_number_id}
                onChange={(e) => setFormData({ ...formData, phone_number_id: e.target.value })}
                required
                helperText="ID do número de telefone na Cloud API"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
          </Grid>
          </Box>

          {/* Seção: Credenciais */}
          <Divider sx={{ my: 3, borderColor: 'rgba(139, 92, 246, 0.2)' }} />
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ 
              color: '#8b5cf6', 
              fontWeight: 600, 
              mb: 2,
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Credenciais
            </Typography>
            <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Access Token *"
                type="password"
                value={formData.access_token}
                onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                required={!selectedConfig}
                helperText={selectedConfig ? "Deixe em branco para manter o token atual" : "Token de acesso da Cloud API"}
                InputProps={{
                  startAdornment: <KeyIcon sx={{ mr: 1, color: '#8b5cf6', fontSize: 20 }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número de Telefone *"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                required
                helperText="Ex: +5511999999999"
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número para Exibição"
                value={formData.display_phone_number}
                onChange={(e) => setFormData({ ...formData, display_phone_number: e.target.value })}
                helperText="Número formatado para exibição"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Account ID"
                value={formData.business_account_id}
                onChange={(e) => setFormData({ ...formData, business_account_id: e.target.value })}
                helperText="ID da conta comercial do WhatsApp"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Versão da API"
                value={formData.api_version}
                onChange={(e) => setFormData({ ...formData, api_version: e.target.value })}
                helperText="Ex: v24.0"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
          </Grid>
          </Box>

          {/* Seção: Webhook */}
          <Divider sx={{ my: 3, borderColor: 'rgba(139, 92, 246, 0.2)' }} />
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ 
              color: '#8b5cf6', 
              fontWeight: 600, 
              mb: 2,
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Webhook
            </Typography>
            <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Webhook Verify Token"
                value={formData.webhook_verify_token}
                onChange={(e) => setFormData({ ...formData, webhook_verify_token: e.target.value })}
                helperText="Token para verificação do webhook"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Webhook URL"
                value={formData.webhook_url}
                onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                helperText="URL do webhook (opcional)"
                InputProps={{
                  startAdornment: <LinkIcon sx={{ mr: 1, color: '#8b5cf6', fontSize: 20 }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
          </Grid>
          </Box>

          {/* Seção: Configurações Avançadas */}
          <Divider sx={{ my: 3, borderColor: 'rgba(139, 92, 246, 0.2)' }} />
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ 
              color: '#8b5cf6', 
              fontWeight: 600, 
              mb: 2,
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Configurações Avançadas
            </Typography>
            <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Prioridade"
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                helperText="Menor número = maior prioridade"
                inputProps={{ min: 1 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Limite Diário"
                type="number"
                value={formData.daily_limit}
                onChange={(e) => setFormData({ ...formData, daily_limit: parseInt(e.target.value) || 1000 })}
                helperText="Máximo de mensagens por dia"
                inputProps={{ min: 1 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#ffffff',
                    border: '1px solid #8b5cf6',
                    borderRadius: '8px',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#7c3aed',
                    },
                    '&.Mui-focused': {
                      borderColor: '#8b5cf6',
                      boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.1)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#8b5cf6',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    mt: 0.5,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_primary}
                    onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#8b5cf6',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#8b5cf6',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: '#0f172a', fontWeight: 500 }}>
                    Configuração Principal
                  </Typography>
                }
              />
            </Grid>
          </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '2px solid #8b5cf6',
          backgroundColor: '#f8fafc',
          p: 2,
          px: 3,
          gap: 1.5,
        }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              color: '#64748b',
              borderColor: 'rgba(139, 92, 246, 0.3)',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(139, 92, 246, 0.08)',
                borderColor: 'rgba(139, 92, 246, 0.5)',
                color: '#475569',
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            startIcon={selectedConfig ? <EditIcon /> : <AddIcon />}
            sx={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: '#ffffff',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                boxShadow: '0 6px 16px rgba(139, 92, 246, 0.4)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            {selectedConfig ? 'Salvar Alterações' : 'Criar Configuração'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de seleção de tipo de campanha */}
      <Dialog open={openCampaignTypeDialog} onClose={() => setOpenCampaignTypeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Selecione o tipo de campanha</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  cursor: 'pointer', 
                  border: '2px solid', 
                  borderColor: 'primary.main',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => handleSelectCampaignType('template')}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📋 Templates
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use templates aprovados do WhatsApp (Marketing ou Utilidade). Ideal para campanhas promocionais ou transacionais.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  cursor: 'pointer', 
                  border: '2px solid', 
                  borderColor: 'primary.main',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => handleSelectCampaignType('24h_window')}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ⏰ Janela 24hrs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Envie mensagens de texto simples diretamente pela Cloud API. Disponível apenas na janela de 24 horas após o contato do lead.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCampaignTypeDialog(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de criação/edição de campanha */}
      <Dialog open={openCampaignDialog} onClose={handleCloseCampaignDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCampaignId ? 'Editar Campanha' : `Nova Campanha - ${campaignForm.campaign_type === '24h_window' ? 'Janela 24hrs' : 'Templates'}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome da Campanha *"
                value={campaignForm.name}
                onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                placeholder="Ex: Campanha de Boas-vindas"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={campaignForm.description}
                onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            {/* Campos específicos para campanhas de Janela 24hrs */}
            {campaignForm.campaign_type === '24h_window' && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Configuração Cloud API *</InputLabel>
                    <Select
                      value={campaignForm.config_id}
                      onChange={(e) => setCampaignForm({ ...campaignForm, config_id: e.target.value })}
                      label="Configuração Cloud API *"
                    >
                      {configs.map((config) => (
                        <MenuItem key={config.id} value={config.id}>
                          {config.name} ({config.phone_number}) {config.status !== 'active' ? `[${config.status}]` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Selecione a configuração Cloud API que será usada para enviar as mensagens
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Texto da Mensagem *"
                    value={campaignForm.message_text}
                    onChange={(e) => setCampaignForm({ ...campaignForm, message_text: e.target.value })}
                    multiline
                    rows={6}
                    placeholder="Digite o texto que será enviado para os contatos..."
                    helperText="Mensagem de texto simples que será enviada diretamente pela Cloud API (dentro da janela de 24 horas)"
                  />
                </Grid>
              </>
            )}

            {/* Campos específicos para campanhas de Templates */}
            {campaignForm.campaign_type === 'template' && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Configuração Cloud API *</InputLabel>
                    <Select
                      value={campaignForm.config_id}
                      onChange={async (e) => {
                        const configId = e.target.value;
                        setCampaignForm({ 
                          ...campaignForm, 
                          config_id: configId,
                          template_name: ''
                        });
                        if (configId) {
                          await fetchTemplates(configId);
                        } else {
                          await fetchTemplates();
                        }
                      }}
                      label="Configuração Cloud API *"
                    >
                      <MenuItem value="">Usar configuração principal</MenuItem>
                      {configs.map((config) => (
                        <MenuItem key={config.id} value={config.id}>
                          {config.name} ({config.phone_number}) {config.status !== 'active' ? `[${config.status}]` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Selecione a configuração para visualizar seus templates
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Template *</InputLabel>
                    <Select
                      value={templates.some(t => t.name === campaignForm.template_name) ? campaignForm.template_name : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '__manual__' || value === '') {
                          setCampaignForm({ ...campaignForm, template_name: '' });
                        } else {
                          setCampaignForm({ ...campaignForm, template_name: value });
                        }
                      }}
                      label="Template *"
                      disabled={!campaignForm.config_id && configs.filter(c => c.is_primary).length === 0}
                    >
                      <MenuItem value="__manual__">Digitar manualmente</MenuItem>
                      {templates.length === 0 ? (
                        <MenuItem disabled>
                          {campaignForm.config_id || configs.filter(c => c.is_primary).length > 0
                            ? 'Carregando templates...'
                            : 'Selecione uma configuração Cloud API primeiro'}
                        </MenuItem>
                      ) : (
                        templates.map((t) => (
                          <MenuItem key={t.name} value={t.name}>
                            {t.name} ({t.language})
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Selecione um template da lista ou use &quot;Digitar manualmente&quot;
                    </Typography>
                  </FormControl>
                </Grid>
                {(!campaignForm.template_name || !templates.some(t => t.name === campaignForm.template_name)) && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nome do Template (Manual) *"
                      value={campaignForm.template_name}
                      onChange={(e) => setCampaignForm({ ...campaignForm, template_name: e.target.value })}
                      placeholder="Ex: hello_world"
                      helperText="Digite o nome do template aprovado no WhatsApp Business"
                    />
                  </Grid>
                )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código do Idioma"
                value={campaignForm.template_language_code}
                onChange={(e) => setCampaignForm({ ...campaignForm, template_language_code: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Template *</InputLabel>
                <Select
                  value={campaignForm.template_type}
                  onChange={(e) => setCampaignForm({ 
                    ...campaignForm, 
                    template_type: e.target.value as 'marketing' | 'utility' 
                  })}
                  label="Tipo de Template *"
                >
                  <MenuItem value="marketing">Marketing</MenuItem>
                  <MenuItem value="utility">Utilidade</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Configuração de Template de Utilidade */}
            {campaignForm.template_type === 'utility' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Configuração do Template de Utilidade
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Quantidade de Variáveis *</InputLabel>
                    <Select
                      value={campaignForm.utility_template_config.variable_count}
                      onChange={(e) => setCampaignForm({
                        ...campaignForm,
                        utility_template_config: {
                          ...campaignForm.utility_template_config,
                          variable_count: parseInt(e.target.value as string)
                        }
                      })}
                      label="Quantidade de Variáveis *"
                    >
                      <MenuItem value={1}>1 variável</MenuItem>
                      <MenuItem value={2}>2 variáveis</MenuItem>
                      <MenuItem value={3}>3 variáveis</MenuItem>
                      <MenuItem value={4}>4 variáveis</MenuItem>
                      <MenuItem value={5}>5 variáveis</MenuItem>
                      <MenuItem value={6}>6 variáveis</MenuItem>
                      <MenuItem value={7}>7 variáveis</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Campos de variáveis */}
                {Array.from({ length: campaignForm.utility_template_config.variable_count }).map((_, index) => (
                  <Grid item xs={12} key={index}>
                    <TextField
                      fullWidth
                      label={`Variável ${index + 1} *`}
                      value={campaignForm.utility_template_config.variables[index] || ''}
                      onChange={(e) => {
                        const newVariables = [...campaignForm.utility_template_config.variables];
                        newVariables[index] = e.target.value;
                        setCampaignForm({
                          ...campaignForm,
                          utility_template_config: {
                            ...campaignForm.utility_template_config,
                            variables: newVariables
                          }
                        });
                      }}
                      placeholder={`Digite o valor da variável ${index + 1}`}
                    />
                  </Grid>
                ))}

                {/* Opções de mídia e botão */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={campaignForm.utility_template_config.has_video}
                        onChange={(e) => setCampaignForm({
                          ...campaignForm,
                          utility_template_config: {
                            ...campaignForm.utility_template_config,
                            has_video: e.target.checked,
                            has_image: e.target.checked ? false : campaignForm.utility_template_config.has_image
                          }
                        })}
                      />
                    }
                    label="Template contém vídeo no header"
                  />
                </Grid>

                {campaignForm.utility_template_config.has_video && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="URL do Vídeo *"
                      value={campaignForm.utility_template_config.video_url}
                      onChange={(e) => setCampaignForm({
                        ...campaignForm,
                        utility_template_config: {
                          ...campaignForm.utility_template_config,
                          video_url: e.target.value
                        }
                      })}
                      placeholder="https://exemplo.com/video.mp4"
                      helperText="URL pública do vídeo (deve ser acessível publicamente)"
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={campaignForm.utility_template_config.has_image}
                        onChange={(e) => setCampaignForm({
                          ...campaignForm,
                          utility_template_config: {
                            ...campaignForm.utility_template_config,
                            has_image: e.target.checked,
                            has_video: e.target.checked ? false : campaignForm.utility_template_config.has_video
                          }
                        })}
                      />
                    }
                    label="Template contém imagem no header"
                  />
                </Grid>

                {campaignForm.utility_template_config.has_image && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="URL da Imagem *"
                      value={campaignForm.utility_template_config.image_url}
                      onChange={(e) => setCampaignForm({
                        ...campaignForm,
                        utility_template_config: {
                          ...campaignForm.utility_template_config,
                          image_url: e.target.value
                        }
                      })}
                      placeholder="https://exemplo.com/imagem.jpg"
                      helperText="URL pública da imagem (deve ser acessível publicamente)"
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={campaignForm.utility_template_config.has_button}
                        onChange={(e) => setCampaignForm({
                          ...campaignForm,
                          utility_template_config: {
                            ...campaignForm.utility_template_config,
                            has_button: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Template contém botão"
                  />
                </Grid>

                {campaignForm.utility_template_config.has_button && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo de Botão</InputLabel>
                        <Select
                          value={campaignForm.utility_template_config.button_sub_type}
                          onChange={(e) => setCampaignForm({
                            ...campaignForm,
                            utility_template_config: {
                              ...campaignForm.utility_template_config,
                              button_sub_type: e.target.value as 'url' | 'quick_reply'
                            }
                          })}
                          label="Tipo de Botão"
                        >
                          <MenuItem value="url">URL</MenuItem>
                          <MenuItem value="quick_reply">Quick Reply</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Texto do Botão *"
                        value={campaignForm.utility_template_config.button_text}
                        onChange={(e) => setCampaignForm({
                          ...campaignForm,
                          utility_template_config: {
                            ...campaignForm.utility_template_config,
                            button_text: e.target.value
                          }
                        })}
                        placeholder="Ex: Clique aqui"
                      />
                    </Grid>
                  </>
                )}
              </>
            )}

            {/* Mídia no Header (apenas para templates de marketing) */}
            {campaignForm.template_type === 'marketing' && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={campaignForm.has_media_header}
                        onChange={(e) => setCampaignForm({ ...campaignForm, has_media_header: e.target.checked })}
                      />
                    }
                    label="Template contém mídia no header (vídeo, imagem ou documento)"
                  />
                </Grid>

                {campaignForm.has_media_header && (
                  <>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo de Mídia</InputLabel>
                        <Select
                          value={campaignForm.media_type}
                          onChange={(e) => setCampaignForm({ ...campaignForm, media_type: e.target.value as any })}
                          label="Tipo de Mídia"
                        >
                          <MenuItem value="video">Vídeo</MenuItem>
                          <MenuItem value="image">Imagem</MenuItem>
                          <MenuItem value="document">Documento</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="URL da Mídia *"
                        value={campaignForm.media_url}
                        onChange={(e) => setCampaignForm({ ...campaignForm, media_url: e.target.value })}
                        placeholder="https://exemplo.com/video.mp4"
                        helperText="URL pública do arquivo de mídia (deve ser acessível publicamente)"
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
              </>
            )}

            {/* Campos comuns para ambos os tipos de campanha */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Fonte dos Contatos *</InputLabel>
                <Select
                  value={campaignForm.source_type}
                  onChange={(e) => setCampaignForm({ ...campaignForm, source_type: e.target.value as any })}
                  label="Fonte dos Contatos *"
                  disabled={!!editingCampaignId}
                >
                  <MenuItem value="csv">Upload de CSV</MenuItem>
                  <MenuItem value="txt">Upload de TXT</MenuItem>
                  <MenuItem value="crm_stage">Estágio do CRM</MenuItem>
                  <MenuItem value="manual">Digitar Números</MenuItem>
                  <MenuItem value="all_contacts">Todos os Contatos</MenuItem>
                  <MenuItem value="contacts_24h">Contatos 24hrs</MenuItem>
                  <MenuItem value="contacts_36h">Contatos 36hrs</MenuItem>
                  <MenuItem value="contacts_48h">Contatos 48hrs</MenuItem>
                </Select>
                {editingCampaignId && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Fonte de contatos não pode ser alterada após criação
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Delay entre envios (segundos)"
                type="number"
                value={campaignForm.delay_between_sends}
                onChange={(e) => setCampaignForm({ ...campaignForm, delay_between_sends: parseInt(e.target.value) || 5 })}
                inputProps={{ min: 1, max: 60 }}
                helperText="Mínimo: 1s, Máximo: 60s"
              />
            </Grid>

            {campaignForm.source_type === 'csv' && (
              <Grid item xs={12}>
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {csvFile ? csvFile.name : 'Selecionar arquivo CSV'}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  O CSV deve conter uma coluna com os números de telefone (55 + DDD + número)
                </Typography>
              </Grid>
            )}

            {campaignForm.source_type === 'txt' && (
              <Grid item xs={12}>
                <input
                  type="file"
                  accept=".txt"
                  ref={txtFileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleTxtFileChange}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => txtFileInputRef.current?.click()}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {txtFile ? txtFile.name : 'Selecionar arquivo TXT'}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  O arquivo TXT deve conter um número por linha (55 + DDD + número)
                </Typography>
              </Grid>
            )}

            {campaignForm.source_type === 'crm_stage' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Estágio do CRM *</InputLabel>
                  <Select
                    value={campaignForm.stage_id}
                    onChange={(e) => setCampaignForm({ ...campaignForm, stage_id: e.target.value })}
                    label="Estágio do CRM *"
                  >
                    {crmStages.length === 0 ? (
                      <MenuItem disabled>Nenhum estágio encontrado</MenuItem>
                    ) : (
                      crmStages.map((stage) => (
                        <MenuItem key={stage.id} value={stage.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                bgcolor: stage.color || '#ccc' 
                              }} 
                            />
                            {stage.name} ({stage.leads_count} leads)
                            {stage.crm_pipelines && (
                              <Typography variant="caption" color="text.secondary">
                                - {stage.crm_pipelines.name}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Apenas leads com WhatsApp serão incluídos
                </Typography>
              </Grid>
            )}

            {campaignForm.source_type === 'manual' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Números de Telefone *"
                  value={campaignForm.phone_numbers}
                  onChange={(e) => setCampaignForm({ ...campaignForm, phone_numbers: e.target.value })}
                  multiline
                  rows={4}
                  placeholder="5511999999999&#10;5521988888888&#10;5531977777777"
                  helperText="Um número por linha, ou separados por vírgula"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Agendar Campanha (Opcional)"
                value={campaignForm.scheduled_at}
                onChange={(e) => setCampaignForm({ ...campaignForm, scheduled_at: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Deixe em branco para iniciar imediatamente após criar"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCampaignDialog} disabled={creatingCampaign}>
            Cancelar
          </Button>
          <Button 
            onClick={handleCreateCampaign} 
            variant="contained"
            disabled={creatingCampaign}
            startIcon={creatingCampaign ? <CircularProgress size={20} /> : (editingCampaignId ? <EditIcon /> : <AddIcon />)}
          >
            {creatingCampaign ? (editingCampaignId ? 'Atualizando...' : 'Criando...') : (editingCampaignId ? 'Atualizar Campanha' : 'Criar Campanha')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Detalhes da Campanha */}
      <Dialog
        open={openCampaignDetailsDialog}
        onClose={() => setOpenCampaignDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalhes da Campanha: {selectedCampaignForDetails?.name}
        </DialogTitle>
        <DialogContent>
          {selectedCampaignForDetails && (
            <Box>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Informações Gerais</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Nome</Typography>
                    <Typography variant="body1">{selectedCampaignForDetails.name}</Typography>
                  </Box>
                  {selectedCampaignForDetails.description && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Descrição</Typography>
                      <Typography variant="body1">{selectedCampaignForDetails.description}</Typography>
                    </Box>
                  )}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      label={selectedCampaignForDetails.status}
                      color={getStatusColor(selectedCampaignForDetails.status) as any}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Template</Typography>
                    <Typography variant="body1">{selectedCampaignForDetails.template_name}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Idioma do Template</Typography>
                    <Typography variant="body1">{selectedCampaignForDetails.template_language_code}</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Delay entre Envios</Typography>
                    <Typography variant="body1">{selectedCampaignForDetails.delay_between_sends} segundos</Typography>
                  </Box>
                  {selectedCampaignForDetails.scheduled_at && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Agendada para</Typography>
                      <Typography variant="body1">{formatDate(selectedCampaignForDetails.scheduled_at)}</Typography>
                    </Box>
                  )}
                  {selectedCampaignForDetails.started_at && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Iniciada em</Typography>
                      <Typography variant="body1">{formatDate(selectedCampaignForDetails.started_at)}</Typography>
                    </Box>
                  )}
                  {selectedCampaignForDetails.completed_at && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Concluída em</Typography>
                      <Typography variant="body1">{formatDate(selectedCampaignForDetails.completed_at)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Criada em</Typography>
                    <Typography variant="body1">{formatDate(selectedCampaignForDetails.created_at)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Estatísticas</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h4" color="primary">
                            {selectedCampaignForDetails.total_contacts}
                          </Typography>
                          <Typography variant="body2">Total de Contatos</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h4" color="success.main">
                            {selectedCampaignForDetails.sent_count}
                          </Typography>
                          <Typography variant="body2">Enviadas</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h4" color="info.main">
                            {selectedCampaignForDetails.delivered_count}
                          </Typography>
                          <Typography variant="body2">Entregues</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h4" color="error.main">
                            {selectedCampaignForDetails.failed_count}
                          </Typography>
                          <Typography variant="body2">Falhas</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h4" color="primary">
                            {selectedCampaignForDetails.progress_percentage.toFixed(1)}%
                          </Typography>
                          <Typography variant="body2">Progresso</Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={selectedCampaignForDetails.progress_percentage} 
                            sx={{ mt: 1, height: 8, borderRadius: 4 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  {selectedCampaignForDetails.error_message && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="error">
                        <Typography variant="body2" fontWeight="bold">Erro:</Typography>
                        <Typography variant="body2">{selectedCampaignForDetails.error_message}</Typography>
                      </Alert>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCampaignDetailsDialog(false)}>Fechar</Button>
          {selectedCampaignForDetails?.status === 'draft' && (
            <Button 
              variant="contained" 
              startIcon={<EditIcon />}
              onClick={() => {
                setOpenCampaignDetailsDialog(false);
                if (selectedCampaignForDetails) {
                  handleEditCampaign(selectedCampaignForDetails);
                }
              }}
            >
              Editar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppCloudApi;

